import { Switch } from 'antd'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { API } from '@/api'
import { ServerDomainInfo, Volume } from '@/api/type-server'
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { Button, Loading, Trans } from '@/components'
import { ORDER_CATALOG, OrderStatus } from '@/consts/order'
import { TeamRole } from '@/consts/team'
import DriveVolumeSection from '@/pages/space-mgr/components/drive-volume-section.tsx'
import ImageVolumeSection from '@/pages/space-mgr/components/image-volume-section.tsx'
import SnapVolumeSection from '@/pages/space-mgr/components/snap-volume-section.tsx'
import { VolumeUsageSegmentedBar } from '@/pages/space-mgr/components/volume-usage-segmented-bar'
import { useTeamStore, useTeamUserRole } from '@/stores/team'
import { formatDatePreset } from '@/utils/formatter'
import { formatGB, getEffectiveQuota } from '@/utils/storage'
import { cn } from '@/utils/style'

import TeamVolumeQuotaModal from '../components/TeamVolumeQuotaModal'

export function TeamSharedSpaceView() {
  const teamStore = useTeamStore()
  const userRole = useTeamUserRole()
  const canModify = userRole && [TeamRole.Owner, TeamRole.Admin].includes(userRole)

  const teamTokenData = teamStore.teamTokenData

  const [loading, setLoading] = useState(true)
  const [volumeQuotaModalOpen, setVolumeQuotaModalOpen] = useState(false)
  const [expireDate, setExpireDate] = useState<number | null>(null)
  const [isExpired, setIsExpired] = useState(false)
  const [purgeDate, setPurgeDate] = useState<number | null>(null)
  const [autoRenewal, setAutoRenewal] = useState(false)
  const [updatingAutoRenewal, setUpdatingAutoRenewal] = useState(false)

  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])
  const [snapshotVolumes, setSnapshotVolumes] = useState<Volume[]>()
  const [cloudVolumes, setCloudVolumes] = useState<Volume[]>()
  const [imageVolumes, setImageVolumes] = useState<Volume[]>()
  const [partVolumeQuotaInGB, setPartVolumeQuotaInGB] = useState(0)

  const getDomains = () => {
    setLoading(true)
    API.getDomainList()
      .then((res: any) => {
        if (res.code === 0) {
          const filteredDomainInfos = (res.domainInfos || []).filter(
            (info: any) => info.domain !== 3,
          )
          setDomainInfos(filteredDomainInfos)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const loadData = async () => {
    if (!teamTokenData?.teamResID) return

    setLoading(true)
    try {
      // Fetch team info to get shared volumes and expiration
      const teamInfoRes = await API.getTeamInfo({ res_id: teamTokenData.teamResID })

      // Fetch team shared storage order (catalog=9) for auto renewal status
      const orderRes = await API.getOrders({
        catalog: ORDER_CATALOG.TEAM_SHARED_STORAGE,
        status_filter: OrderStatus.ALL,
      })
      const orders = orderRes?.orders || []
      const latestOrder = orders[orders.length - 1]
      if (latestOrder) {
        setAutoRenewal(latestOrder.autoRenewal || false)
      }

      if (teamInfoRes?.team?.volumes) {
        const volumes = teamInfoRes.team.volumes

        // Get expiration from volumes.expireTime (accessible by all roles)
        let isOrderExpired = false
        if (volumes.expireTime?.seconds) {
          const expireMs = volumes.expireTime.seconds * 1000
          isOrderExpired = expireMs < Date.now()
          setExpireDate(expireMs)
          setIsExpired(isOrderExpired)

          // Set purge date if expired: expireDate + 30 days
          if (isOrderExpired) {
            setPurgeDate(dayjs(expireMs).add(30, 'day').valueOf())
          }
        }

        // allocPart is the total team shared storage
        if (volumes.allocPart) {
          setPartVolumeQuotaInGB(getEffectiveQuota(volumes.allocPart, isOrderExpired))
        }

        // subParts are the subdivisions (snapshot/datadrive by region)
        if (volumes.subParts) {
          setSnapshotVolumes(volumes.subParts.filter((v: Volume) => v.type === 2))
          setCloudVolumes(volumes.subParts.filter((v: Volume) => v.type === 1))
          setImageVolumes(volumes.subParts.filter((v: Volume) => v.type === 3))
        }
      }
    } catch (error) {
      console.error('Failed to load team shared space data:', error)
    } finally {
      setLoading(false)
    }
  }

  const hasImageVolumes = (imageVolumes?.length ?? 0) > 0
  const hasSnapshotVolumes = (snapshotVolumes?.length ?? 0) > 0

  const handleAutoRenewalToggle = async (checked: boolean) => {
    setUpdatingAutoRenewal(true)
    try {
      await API.updateOrderAutoRenewal({
        auto_renewal: checked,
        catalog: ORDER_CATALOG.TEAM_SHARED_STORAGE,
        type: 1,
      })
      setAutoRenewal(checked)
    } catch (error) {
      console.error('Failed to update auto renewal:', error)
      setAutoRenewal(!checked)
    } finally {
      setUpdatingAutoRenewal(false)
    }
  }

  useEffect(() => {
    getDomains()
    loadData()
  }, [])

  // Calculate image volume available quota
  const snapshotQuotaSum = (snapshotVolumes || []).reduce(
    (sum, vol) => sum + getEffectiveQuota(vol, isExpired),
    0,
  )
  const cloudQuotaSum = (cloudVolumes || []).reduce(
    (sum, vol) => sum + getEffectiveQuota(vol, isExpired),
    0,
  )

  const imageVolumeAvailableQuota = Math.max(
    0,
    partVolumeQuotaInGB - snapshotQuotaSum - cloudQuotaSum,
  )

  // Calculate total actual usage from all volumes
  const totalActualUsageInGB = useMemo(() => {
    const allVolumes = [
      ...(snapshotVolumes || []),
      ...(cloudVolumes || []),
      ...(imageVolumes || []),
    ]
    return allVolumes.reduce((sum, volume) => sum + volume.usage_mb / 1024, 0)
  }, [snapshotVolumes, cloudVolumes, imageVolumes])

  return (
    <>
      <div className="card bg-panel-white p-8 h-full overflow-y-auto">
        {/* Header: Back Button + Title + Modify Button */}
        <div className="flex items-center justify-between mb-8 pb-4 border-b border-neutral-100">
          <div className="flex items-center gap-3">
            <Link to="/team/space/admin" className="p-1 bg-panel-gray-100 rounded-md">
              <ArrowLeftIcon className="w-6 h-6 text-tertiary" />
            </Link>
            <div className="h6 gap-2">
              <span className="text-tertiary">
                <Trans i18nKey="space.titleSpaceStorage">Storage Space</Trans>
              </span>
              <span> / </span>
              <span>
                <Trans i18nKey="space.teamLabel">Team</Trans>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {canModify && (
              <Button
                type="secondary"
                className="h-11 min-w-[88px]"
                disabled={!partVolumeQuotaInGB}
                onClick={() => setVolumeQuotaModalOpen(true)}
              >
                <Trans i18nKey="space.modify">Modify</Trans>
              </Button>
            )}
          </div>
        </div>

        {/* Storage Overview Section */}
        <div className="border border-neutral-200 p-8 mb-8 rounded-2xl">
          <div className="t-sb mb-4 flex justify-between">
            <Trans i18nKey="space.teamStorageSpace">Team Storage Space</Trans>
            {userRole === TeamRole.Owner && !isExpired && expireDate && (
              <div className="flex items-center gap-2">
                <span className="t-sb">
                  <Trans i18nKey="space.autoRenewalTitle">Auto Renewal</Trans>
                </span>
                <Switch
                  checked={autoRenewal}
                  loading={updatingAutoRenewal}
                  onChange={handleAutoRenewalToggle}
                  checkedChildren={<Trans i18nKey="space.autoRenewalOn">ON</Trans>}
                  unCheckedChildren={<Trans i18nKey="space.autoRenewalOff">OFF</Trans>}
                />
              </div>
            )}
          </div>

          {partVolumeQuotaInGB === 0 ? (
            <div className="h-2 bg-neutral-100 rounded-full animate-pulse" />
          ) : (
            <VolumeUsageSegmentedBar
              snapshotVolumes={snapshotVolumes}
              cloudVolumes={cloudVolumes}
              totalQuota={partVolumeQuotaInGB}
              isExpired={isExpired}
              imageOnSpaceBilling={hasImageVolumes}
              snapshotOnSpaceBilling={hasSnapshotVolumes}
            />
          )}

          {/* Usage + Legend */}
          <div className="flex justify-between items-center mt-4">
            <div className="flex gap-4 items-center">
              <div className="text-secondary t-sb">
                <Trans
                  i18nKey="space.usageFormat"
                  values={{
                    usage: formatGB(totalActualUsageInGB, {
                      dynamicDecimals: true,
                      includeUnit: false,
                    }),
                    quota: formatGB(partVolumeQuotaInGB, {
                      dynamicDecimals: true,
                      includeUnit: false,
                    }),
                  }}
                  components={[
                    <span className="text-primary" key="usage" />,
                    <span className="text-primary" key="quota" />,
                  ]}
                />
              </div>

              <div className="h-3 w-0.5 bg-neutral-200" />

              {/* Expiration + Data Purge */}
              <div className="flex items-center gap-4 t-sb">
                <div className="text-secondary">
                  <Trans i18nKey="space.expireOn">Expires on</Trans>{' '}
                  <span className="text-primary">
                    {expireDate ? formatDatePreset(expireDate) : '-'}
                  </span>
                </div>
                {isExpired && purgeDate && (
                  <>
                    <div className="h-3 w-0.5 bg-neutral-200" />
                    <div className="text-secondary">
                      <Trans i18nKey="space.dataPurgeOn">Data Purge on</Trans>{' '}
                      <span className="text-error">{formatDatePreset(purgeDate)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* Legend */}
            <div className="flex items-center gap-4 t-sm">
              {hasSnapshotVolumes && (
                <div className="flex items-center gap-2">
                  <div
                    className={cn('w-3 h-3', isExpired ? 'bg-bar-tertiary' : 'bg-btn-tertiary')}
                  />
                  <span>
                    <Trans i18nKey="space.volumeItemSubTitleSnapshot">Snapshot</Trans>
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <div
                  className={cn('w-3 h-3', isExpired ? 'bg-bar-tertiary/60' : 'bg-btn-secondary')}
                />
                <span>
                  <Trans i18nKey="space.volumeItemSubTitleCloudStorage">Datadrive</Trans>
                </span>
              </div>
              {hasImageVolumes && (
                <div className="flex items-center gap-2">
                  <div className={cn('w-3 h-3', isExpired ? 'bg-tertiary' : 'bg-active')} />
                  <span>
                    <Trans i18nKey="space.volumeItemSubTitleImage">Image</Trans>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Expired Warning - Only show for Owner */}
          {isExpired && userRole === TeamRole.Owner && (
            <div className="text-error t-sm mt-4">
              <Trans i18nKey="space.volumeItemExpired">
                Your storage space has expired, please upgrade your plan
              </Trans>
            </div>
          )}
        </div>

        {/* Quota Section */}
        <div>
          {loading ? (
            <div className="w-full flex items-center justify-center p-8">
              <Loading />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {hasImageVolumes && (
                <ImageVolumeSection
                  volumes={imageVolumes || []}
                  availableQuota={imageVolumeAvailableQuota}
                  isExpired={isExpired}
                  warningMessages={{
                    critical: t('space.volumeItemTeamNoStorage'),
                    low: t('space.volumeItemTeamLowStorage'),
                  }}
                />
              )}
              {hasSnapshotVolumes && (
                <SnapVolumeSection
                  volumes={snapshotVolumes || []}
                  isExpired={isExpired}
                  warningMessages={{
                    critical: t('space.volumeItemTeamNoStorage'),
                    low: t('space.volumeItemTeamLowStorage'),
                  }}
                />
              )}
              <DriveVolumeSection
                volumes={cloudVolumes || []}
                domainInfos={domainInfos}
                isExpired={isExpired}
                warningMessages={{
                  critical: t('space.volumeItemTeamNoStorage'),
                  low: t('space.volumeItemTeamLowStorage'),
                }}
              />
            </div>
          )}
        </div>
      </div>

      {volumeQuotaModalOpen && (
        <TeamVolumeQuotaModal
          open={volumeQuotaModalOpen}
          onOk={() => {
            setVolumeQuotaModalOpen(false)
            loadData()
          }}
          onClose={() => setVolumeQuotaModalOpen(false)}
          data={{
            snapshotVolumes: snapshotVolumes,
            cloudVolumes: cloudVolumes,
            imageVolumes: imageVolumes,
            domainInfos: domainInfos,
            partVolumeQuotaInGB,
            isExpired,
            expireDate,
          }}
        />
      )}
    </>
  )
}
